import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ApexTooltip } from "ng-apexcharts";
import { locale as italian } from "../../../../../i18n/it";

import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import moment from "moment";
import { ICustomerCommissions, ICustomerSingle } from "../../../customer.interface";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";
import { AuthenticationService } from "../../../../../auth/service";
import { UtilsService } from "../../../../../auth/service/utils.service";
import { environment } from "../../../../../../environments/environment";

import { ApexNonAxisChartSeries, ApexResponsive, ApexChart } from "ng-apexcharts";
import { CustomerService } from "../../../customer.service";

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  tooltip: ApexTooltip;
};

@Component({
  selector: "app-customer-detail",
  templateUrl: "./customer-detail.component.html",
  styleUrls: ["./customer-detail.component.scss"],
})
export class CustomerDetailComponent implements OnInit {

  public contentHeader: IContentHeader = null;
  public customer_id: number;
  public customer: ICustomerSingle;
  public tableLimit: number = 10;
  public searchKey: string = "";
  public customerCommission: ICustomerCommissions[] = [];
  public filteredCommissions: ICustomerCommissions[] = [];
  public totalRevenue: number = 0;
  public totalCost: number = 0;
  public filterStatusCommission: number = -1;
  public availableYears: number[] = [];
  public availableYearsGraph: number[] = [];
  public selectedYear: number = moment().year();
  public selectedYearGraph: number = moment().year();
  public economicClient: {
    total_real_profit: string;
    total_margin_currency: string;
    client_total_margin_currency: string;
    client_total_real_profit: string;
  };

  public loadingCommissions: boolean = true;

  public chartOptions: ChartOptions = {
    series: [],
    chart: {
      width: 380,
      type: "pie",
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val ? val.toLocaleString("it-IT", { style: "currency", currency: "EUR" }) : "-";
        },
      },
    },
    labels: [italian.data.ANALYTICS.CLIENT_SALE_VOLUME, italian.data.ANALYTICS.COMPANY_SALE_VOLUME],
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  constructor(
    private _activatedRoute: ActivatedRoute,
    public _authenticationService: AuthenticationService,
    public router: Router,
    public _utilService: UtilsService,
    public _customerService: CustomerService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    this._utilService.disableAutoHideLoading();
    this._activatedRoute.paramMap.subscribe(async (params: ParamMap) => {
      const customerId: string = params.get("id");
      if (customerId) {
        if (!this._authenticationService.hasCapability("edit_customer")) {
          this._utilService.hideLoading();
          await this.router.navigateByUrl("/dashboard/main");
          return false;
        }
        this.customer_id = Number(customerId);
      }
    });

    this.contentHeader = {
      headerTitle: "MENU.CUSTOMERS",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: "MENU.CUSTOMERS",
            isLink: true,
            link: "/dashboard/customers",
          },
          {
            name: "CUSTOMER_DETAIL",
            isLink: false,
          },
        ],
      },
    };
    for (let i: number = 2023; i <= this.selectedYear + 1; i++) {
      this.availableYears.push(i);
      this.availableYearsGraph.push(i);
    }
    this.availableYearsGraph.pop();
  }

  async ngOnInit() {
    await this.loadCustomer();
  }

  filterCommissions() {
    let filterCommissions: ICustomerCommissions[] = this.customerCommission;
    if (this.filterStatusCommission > -1) {
      const closed = Boolean(this.filterStatusCommission);
      filterCommissions = filterCommissions.filter((item: ICustomerCommissions) => item.closed == closed);
    }
    this.filteredCommissions = filterCommissions;
  }

  async loadCustomer() {
    this.totalCost = 0;
    this.totalRevenue = 0;
    this.customer = await this._customerService.getCustomer(this.customer_id);
    if (!this.customer || typeof this.customer === "string") {
      this._utilService.hideLoading();
      await this._utilService.showToastrError("CUSTOMER_NOT_FOUND");
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    if (this.customer.logo) {
      this.customer.logo = environment.CDN_URL + "/" + this.customer.logo;
    }
    await this.loadCustomerGraph();
    this.contentHeader.headerTitle = this.customer.name;

    this._utilService.hideLoading();

    if (this._authenticationService.hasCapability("show_menu_commission")) {
      await this.loadCustomerCommission();
    }

    this._changeDetectorRef.detectChanges();
  }

  async loadCustomerGraph() {
    await this._customerService.getCustomerEconomicGraph(this.customer_id, this.selectedYearGraph).then((resp: any) => {
      this.economicClient = resp;
      this.charInit();
    });
  }

  async charInit() {
    this.chartOptions.series = [];
    if (this.economicClient.client_total_real_profit && this.economicClient.total_real_profit) {
      const clientTotalRealProfit: number = Number(this.economicClient.client_total_real_profit);
      if (clientTotalRealProfit > 0) {
        this.chartOptions.series.push(clientTotalRealProfit);
      }

      const totalRealProfit: number = Number(this.economicClient.total_real_profit);
      if (totalRealProfit > 0) {
        this.chartOptions.series.push(Number(this.economicClient.total_real_profit));
      }
    }
  }

  async loadCustomerCommission() {
    this.totalCost = 0;
    this.totalRevenue = 0;
    this.loadingCommissions = true;
    await this._customerService.getCustomerCommissionAnalytics(this.customer_id, this.selectedYear).then((response: ICustomerCommissions[]) => {

      const today = moment()
      response = (response || []).map((commission: ICustomerCommissions) => {
        commission.analytics = {
          employees: commission.general_analytics.employees,
          estimate_cost: commission.general_analytics.general_estimate_cost,
          estimate_profit: commission.general_analytics.general_estimate_profit,
          margin_currency: commission.general_analytics.general_margin_currency,
          margin_percentage: commission.general_analytics.general_margin_percentage,
          real_cost: commission.general_analytics.general_real_cost,
          real_profit: commission.general_analytics.general_real_profit,
          products: commission.general_analytics.products,
        };
        delete commission.general_analytics;

        if(!!commission.orders && Array.isArray(commission.orders) ) {
          commission.orders = (commission.orders || []).sort((a: any, b: any) => {
            a = moment(a.end_date);
            b = moment(b.end_date);
            return a > b ? 1 : (a < b ? -1 : 0);
          });
        }

        commission.closed = moment(commission.end_date).isBefore(today)

        return commission;
      });

      this.customerCommission = response;
      if (this.customerCommission && this.customerCommission.length > 0) {
        this.customerCommission.map((commission: ICustomerCommissions) => {
          if (!!commission.analytics.real_cost) {
            this.totalCost += commission.analytics.real_cost;
          }
          if (!!commission.analytics.real_profit) {
            this.totalRevenue += commission.analytics.real_profit;
          }
        });
      }
      this.loadingCommissions = false;
    });
    this.filterCommissions();
  }

  updateTableLimit(event: { target: { value: string } }) {
    this.tableLimit = Number(event.target.value);
  }

  formatTooltip(employees_names: string[]) {
    if (employees_names && employees_names.length > 0) {
      return employees_names.join(", ");
    }
  }
}
